import axios from 'axios';

const api = axios.create({
  baseURL: '',
});

// Auth endpoints that should not send/intercept tokens
const AUTH_ENDPOINTS = ['/api/auth/login', '/api/auth/signup'];

// Attach JWT token to every request (except auth endpoints)
api.interceptors.request.use((config) => {
  const url = config.url || '';
  if (!AUTH_ENDPOINTS.some((ep) => url.startsWith(ep))) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle 401 from expired/invalid tokens (not from login attempts)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!AUTH_ENDPOINTS.some((ep) => url.startsWith(ep))) {
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('auth-expired'));
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),
  signup: (username: string, password: string, email: string) =>
    api.post('/api/auth/signup', { username, password, email }),
  getMe: () => api.get('/api/auth/me'),
};

// Portfolios
export const portfolioApi = {
  list: () => api.get('/api/portfolios/'),
  create: (data: any) => api.post('/api/portfolios/', data),
  update: (id: number, data: any) => api.put(`/api/portfolios/${id}`, data),
  delete: (id: number) => api.delete(`/api/portfolios/${id}`),
};

// Accounts
export const accountApi = {
  list: () => api.get('/api/portfolios/accounts'),
  create: (data: any) => api.post('/api/portfolios/accounts', data),
  update: (id: number, data: any) => api.put(`/api/portfolios/accounts/${id}`, data),
  delete: (id: number) => api.delete(`/api/portfolios/accounts/${id}`),
};

// Holdings
export const holdingApi = {
  list: (portfolio?: string) =>
    api.get('/api/holdings/', { params: portfolio ? { portfolio } : {} }),
  get: (id: number) => api.get(`/api/holdings/${id}`),
  create: (data: any) => api.post('/api/holdings/', data),
  update: (id: number, data: any) => api.put(`/api/holdings/${id}`, data),
  delete: (id: number) => api.delete(`/api/holdings/${id}`),
};

// Indicators
export const indicatorApi = {
  list: () => api.get('/api/indicators/'),
};

// Users
export const userApi = {
  list: () => api.get('/api/users/'),
  get: (id: number) => api.get(`/api/users/${id}`),
  create: (data: any) => api.post('/api/users/', data),
  update: (id: number, data: any) => api.put(`/api/users/${id}`, data),
  delete: (id: number) => api.delete(`/api/users/${id}`),
};

// Transactions
export const transactionApi = {
  list: (params?: { account?: string; ticker?: string; show_all?: boolean }) =>
    api.get('/api/transactions/', { params }),
  get: (id: number) => api.get(`/api/transactions/${id}`),
  create: (data: any) => api.post('/api/transactions/', data),
  update: (id: number, data: any) => api.put(`/api/transactions/${id}`, data),
  delete: (id: number) => api.delete(`/api/transactions/${id}`),
  dailySummary: () => api.get('/api/transactions/reports/daily-summary'),
  capReturn: () => api.get('/api/transactions/reports/cap-return'),
  volumeMovers: () => api.get('/api/transactions/reports/volume-movers'),
  avgCost: (ticker: string, account?: string, fromDate?: string) => api.get('/api/transactions/reports/avg-cost', { params: { ticker, ...(account ? { account } : {}), ...(fromDate ? { from_date: fromDate } : {}) } }),
  avgSell: (ticker: string, account?: string, fromDate?: string) => api.get('/api/transactions/reports/avg-sell', { params: { ticker, ...(account ? { account } : {}), ...(fromDate ? { from_date: fromDate } : {}) } }),
  afterHours: () => api.get('/api/transactions/reports/after-hours'),
};

// Watch Lists
export const watchlistApi = {
  list: () => api.get('/api/watchlists/'),
  create: (data: any) => api.post('/api/watchlists/', data),
  update: (id: number, data: any) => api.put(`/api/watchlists/${id}`, data),
  delete: (id: number) => api.delete(`/api/watchlists/${id}`),
};

// Form4
export const form4Api = {
  latestBuy: () => api.get('/api/form4/latest-buy'),
  latestSell: () => api.get('/api/form4/latest-sell'),
  weeklyBuy: () => api.get('/api/form4/weekly-buy'),
  weeklySell: () => api.get('/api/form4/weekly-sell'),
  search: (params: { ticker?: string; form4id?: string; min_amount?: number }) =>
    api.get('/api/form4/search', { params }),
  dashboard: () => api.get('/api/form4/dashboard'),
  popularTickers: () => api.get<string[]>('/api/form4/popular-tickers'),
  dates: () => api.get('/api/form4/dates'),
  outliers: () => api.get('/api/form4/outliers'),
  delete: (id: number) => api.delete(`/api/form4/${id}`),
  deleteByDate: (reportDate: string) => api.delete(`/api/form4/by-date/${reportDate}`),
  deleteByDateRange: (startDate: string, endDate: string) => api.delete('/api/form4/by-date-range', { params: { start_date: startDate, end_date: endDate } }),
  postToX: (text: string, likeReplies: boolean = false, likeWait: number = 3600,
            chartTicker?: string, chartTxnDate?: string, chartTxnType?: string) =>
    api.post('/api/form4/post-to-x', {
      text, like_replies: likeReplies, like_wait: likeWait,
      ...(chartTicker ? { chart_ticker: chartTicker, chart_txn_date: chartTxnDate, chart_txn_type: chartTxnType } : {}),
    }),
  likeNotifications: (maxLikes: number = 50) =>
    api.post('/api/form4/like-notifications', null, { params: { max_likes: maxLikes } }),
  dailyPicks: (pickDate?: string) =>
    api.get('/api/form4/daily-picks', { params: pickDate ? { pick_date: pickDate } : {} }),
  dailyPicksDates: () => api.get('/api/form4/daily-picks/dates'),
  dailyPicksGenerate: (pickDate?: string) =>
    api.post('/api/form4/daily-picks/generate', null, { params: pickDate ? { pick_date: pickDate } : {} }),
};

// SEDI
export const sediApi = {
  latestBuy: () => api.get('/api/sedi/latest-buy'),
  latestSell: () => api.get('/api/sedi/latest-sell'),
  weeklyBuy: () => api.get('/api/sedi/weekly-buy'),
  weeklySell: () => api.get('/api/sedi/weekly-sell'),
  search: (params: { ticker?: string; min_amount?: number }) =>
    api.get('/api/sedi/search', { params }),
  update: (id: number, data: { ticker?: string; shares?: number; price?: number; amount?: number; totalShares?: number }) =>
    api.put(`/api/sedi/${id}`, data),
  delete: (id: number) => api.delete(`/api/sedi/${id}`),
  bulkDelete: (ids: number[]) => api.post('/api/sedi/bulk-delete', ids),
  deleteByDate: (reportDate: string) => api.delete(`/api/sedi/by-date/${reportDate}`),
  deleteByDateRange: (startDate: string, endDate: string) => api.delete('/api/sedi/by-date-range', { params: { start_date: startDate, end_date: endDate } }),
  dashboard: () => api.get('/api/sedi/dashboard'),
  dates: () => api.get('/api/sedi/dates'),
  outliers: () => api.get('/api/sedi/outliers'),
  missingTickers: () => api.get('/api/sedi/missing-tickers'),
  lookupTicker: (name: string) => api.get('/api/sedi/lookup-ticker', { params: { name } }),
  fixTicker: (data: { name: string; exchange: string; ticker: string }) =>
    api.post('/api/sedi/fix-ticker', data),
  dailyPicks: (pickDate?: string) =>
    api.get('/api/sedi/daily-picks', { params: pickDate ? { pick_date: pickDate } : {} }),
  dailyPicksDates: () => api.get('/api/sedi/daily-picks/dates'),
  dailyPicksGenerate: (pickDate?: string) =>
    api.post('/api/sedi/daily-picks/generate', null, { params: pickDate ? { pick_date: pickDate } : {} }),
};

// Insider Activity
export const insiderApi = {
  portfolio: () => api.get('/api/insider-activity/portfolio'),
  watchlist: () => api.get('/api/insider-activity/watchlist'),
  search: (ticker: string) => api.get('/api/insider-activity/search', { params: { ticker } }),
};

// Institutional Ownership
export const institutionApi = {
  quarterlySummary: () => api.get('/api/institutions/quarterly-summary'),
  portfolio: () => api.get('/api/institutions/portfolio'),
  search: (ticker: string) => api.get('/api/institutions/search', { params: { ticker } }),
  dashboard: () => api.get('/api/institutions/dashboard'),
  dates: () => api.get('/api/institutions/dates'),
  stats: () => api.get('/api/institutions/stats'),
  deleteByDateRange: (start: string, end: string) => api.delete('/api/institutions/by-date-range', { params: { start_date: start, end_date: end } }),
  deleteByFiler: (cik: string) => api.delete(`/api/institutions/by-filer/${cik}`),
  refreshSummary: () => api.post('/api/institutions/refresh-summary'),
  fixPrn: () => api.post('/api/institutions/fix-prn'),
};

// Reference Data
export const quicknoteApi = {
  list: () => api.get('/api/reference/quicknotes'),
  create: (data: any) => api.post('/api/reference/quicknotes', data),
  update: (id: number, data: any) => api.put(`/api/reference/quicknotes/${id}`, data),
  delete: (id: number) => api.delete(`/api/reference/quicknotes/${id}`),
};

export const tickermapperApi = {
  list: () => api.get('/api/reference/tickermappers'),
  create: (data: any) => api.post('/api/reference/tickermappers', data),
  update: (id: number, data: any) => api.put(`/api/reference/tickermappers/${id}`, data),
  delete: (id: number) => api.delete(`/api/reference/tickermappers/${id}`),
};

export const industryMapperApi = {
  list: () => api.get('/api/reference/industry-mappers'),
  create: (data: any) => api.post('/api/reference/industry-mappers', data),
  update: (id: number, data: any) => api.put(`/api/reference/industry-mappers/${id}`, data),
  delete: (id: number) => api.delete(`/api/reference/industry-mappers/${id}`),
};

// Configuration
export const configApi = {
  reasons: () => api.get('/api/configuration/reasons'),
  createReason: (data: any) => api.post('/api/configuration/reasons', data),
  updateReason: (id: number, data: any) => api.put(`/api/configuration/reasons/${id}`, data),
  deleteReason: (id: number) => api.delete(`/api/configuration/reasons/${id}`),
  trackedFilers: () => api.get('/api/configuration/tracked-filers'),
  createTrackedFiler: (data: any) => api.post('/api/configuration/tracked-filers', data),
  updateTrackedFiler: (id: number, data: any) => api.put(`/api/configuration/tracked-filers/${id}`, data),
  deleteTrackedFiler: (id: number) => api.delete(`/api/configuration/tracked-filers/${id}`),
  sectors: () => api.get('/api/configuration/sectors'),
  createSector: (data: any) => api.post('/api/configuration/sectors', data),
  updateSector: (id: number, data: any) => api.put(`/api/configuration/sectors/${id}`, data),
  deleteSector: (id: number) => api.delete(`/api/configuration/sectors/${id}`),
  industries: () => api.get('/api/configuration/industries'),
  createIndustry: (data: any) => api.post('/api/configuration/industries', data),
  updateIndustry: (id: number, data: any) => api.put(`/api/configuration/industries/${id}`, data),
  deleteIndustry: (id: number) => api.delete(`/api/configuration/industries/${id}`),
  stocklists: (search?: string) => api.get('/api/configuration/stocklists', { params: search ? { search } : {} }),
  lookupStocklist: (ticker: string) => api.get('/api/configuration/stocklists/lookup', { params: { ticker } }),
  missingStocklists: (params?: { exchange?: string; search?: string }) => api.get('/api/configuration/stocklists/missing', { params }),
  batchCreateStocklists: (items: any[]) => api.post('/api/configuration/stocklists/batch', items),
  createStocklist: (data: any) => api.post('/api/configuration/stocklists', data),
  updateStocklist: (id: number, data: any) => api.put(`/api/configuration/stocklists/${id}`, data),
  deleteStocklist: (id: number) => api.delete(`/api/configuration/stocklists/${id}`),
  stockSymbols: (params?: { search?: string; exchange?: string }) => api.get('/api/configuration/stock-symbols', { params }),
  missingStockSymbols: (params?: { exchange?: string; search?: string }) => api.get('/api/configuration/stock-symbols/missing', { params }),
  batchCreateStockSymbols: (items: any[]) => api.post('/api/configuration/stock-symbols/batch', items),
  createStockSymbol: (data: any) => api.post('/api/configuration/stock-symbols', data),
  updateStockSymbol: (id: number, data: any) => api.put(`/api/configuration/stock-symbols/${id}`, data),
  deleteStockSymbol: (id: number) => api.delete(`/api/configuration/stock-symbols/${id}`),
  uploadSediFile: (file: File, reportDate: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('report_date', reportDate);
    return api.post('/api/configuration/sedi/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  confirmSediInsert: (file: File, reportDate: string) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/configuration/sedi/confirm?report_date=${reportDate}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  sediTransactions: (reportDate?: string) => api.get('/api/configuration/sedi/transactions', { params: reportDate ? { report_date: reportDate } : {} }),
};

// Insider Ownership
export const insiderOwnershipApi = {
  tickers: () => api.get('/api/insider-ownership/tickers'),
  search: (ticker: string) => api.get('/api/insider-ownership/search', { params: { ticker } }),
  records: (ticker: string) => api.get('/api/insider-ownership/records', { params: { ticker } }),
  lookupName: (ticker: string) => api.get('/api/insider-ownership/lookup-name', { params: { ticker } }),
  upload: (file: File, ticker: string, name: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('ticker', ticker);
    form.append('name', name);
    return api.post('/api/insider-ownership/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  confirm: (file: File, ticker: string, name: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('ticker', ticker);
    form.append('name', name);
    return api.post('/api/insider-ownership/confirm', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export default api;
